import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  Optional,
  SkipSelf,
} from '@angular/core';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {TISA_SPINNER_COMPONENT} from '@tisa-group/client-ui/tokens/spinner-component';
import {SpinnerComponent} from '@tisa-group/client-ui/components/spinner';

export type ButtonPrimitiveAppearance = 'primary' | 'secondary' | 'flat' | 'outline';
export type ButtonPrimitiveLabel = `<ng-content></ng-content>`;

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ButtonPrimitiveComponent {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _loading = false;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  _disabled = false;

  readonly height = 'h-[60px]';

  readonly width: string | null = null;

  @HostBinding(`ButtonPrimitiveLabel`)
  get baseLabelAttribute(): string {
    let labelAttribute = `role='button' attr.aria-disabled='false'`;

    if (this.disabled) {
      labelAttribute += `attr.aria-disabled='true'`;
    }

    return labelAttribute;
  }

  @HostBinding('attr.aria-label')
  get labelAttribute(): string {
    return `${this.baseLabelAttribute} attr.aria-label='Udostępnij'`;
  }

  @Input()
  appearance: ButtonPrimitiveAppearance = 'primary';

  get baseClassName(): string {
    let className =
      'px-4 font-medium transition duration-100 disabled:pointer-events-none rounded-lg disabled:bg-opacity-50 disabled:select-none';

    if (this.height) {
      className += ` ${this.height}`;
    }

    if (this.width) {
      className += ` ${this.width}`;
    }

    return className;
  }

  @HostBinding('class')
  get className(): string {
    switch (this.appearance) {
      case 'secondary':
        return `${this.baseClassName} bg-secondary-500 hover:bg-secondary-200 text-white-500 disabled:text-white-t-500 active:bg-secondary-900`;
      case 'flat':
        return `${this.baseClassName} bg-transparent hover:bg-primary-500 hover:text-white-500 disabled:text-gray-500 active:bg-primary-900`;
      case 'outline':
        return `${this.baseClassName} bg-transparent border border-gray-200 hover:bg-gray-100 hover:border-gray-100 disabled:border-gray-200 disabled:text-gray-500 active:bg-gray-300 active:border-gray-300`;
      case 'primary':
      default:
        return `${this.baseClassName} bg-primary-500 hover:bg-primary-300 text-white-500 disabled:text-white-t-500 active:bg-primary-900`;
    }
  }

  @HostBinding('disabled')
  get disabledAttribute(): boolean {
    if (this.loading && !this.disabled) {
      return true;
    }

    return !(!this.loading && !this.disabled);
  }

  get loading(): boolean {
    return this._loading;
  }

  @Input()
  set loading(value: boolean) {
    this._loading = coerceBooleanProperty(value);
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }

  constructor(
    @Optional()
    @SkipSelf()
    @Inject(TISA_SPINNER_COMPONENT)
    readonly spinnerComponent: any = SpinnerComponent,
    readonly elementRef: ElementRef,
  ) {}
}
